import { useState } from 'react';

export default function ServiceSelection() {
  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Elija su Ruta de Cuidado
          </h3>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Seleccione el servicio de asistencia de salud que mejor se adapte a sus necesidades
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Residential Care Card */}
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-uri-green cursor-pointer group">
            <div className="w-16 h-16 bg-uri-green/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-uri-green transition-colors">
              <i className="ri-home-heart-line text-3xl text-uri-green group-hover:text-white transition-colors"></i>
            </div>
            
            <h4 className="text-2xl font-bold text-gray-900 mb-4">Servicios de Cuidado Residencial</h4>
            <p className="text-gray-600 mb-6">
              Asistencia personalizada de cuidado en casa diseñada para ayudar a sus seres queridos a mantener independencia y comodidad en su propio hogar.
            </p>

            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-uri-green text-xl mt-0.5 flex-shrink-0"></i>
                <div>
                  <span className="text-sm font-semibold text-gray-900">Cuidado de Enfermería Profesional:</span>
                  <span className="text-sm text-gray-700"> Monitoreo de signos vitales, curaciones, administración de medicamentos y seguimiento médico continuo</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-uri-green text-xl mt-0.5 flex-shrink-0"></i>
                <div>
                  <span className="text-sm font-semibold text-gray-900">Apoyo en Actividades Diarias:</span>
                  <span className="text-sm text-gray-700"> Asistencia en tareas domésticas, preparación de alimentos, lavandería y cuidados del hogar</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-uri-green text-xl mt-0.5 flex-shrink-0"></i>
                <div>
                  <span className="text-sm font-semibold text-gray-900">Administración de Medicamentos:</span>
                  <span className="text-sm text-gray-700"> Control de horarios, dosificación precisa y seguimiento de tratamientos médicos</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-uri-green text-xl mt-0.5 flex-shrink-0"></i>
                <div>
                  <span className="text-sm font-semibold text-gray-900">Compañía y Apoyo Emocional:</span>
                  <span className="text-sm text-gray-700"> Conversación, actividades recreativas y acompañamiento para mejorar el bienestar mental</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-uri-green text-xl mt-0.5 flex-shrink-0"></i>
                <div>
                  <span className="text-sm font-semibold text-gray-900">Preparación de Comidas Especiales:</span>
                  <span className="text-sm text-gray-700"> Dietas personalizadas según indicaciones médicas y necesidades nutricionales específicas</span>
                </div>
              </li>
            </ul>

            <button 
              onClick={() => scrollToSection('details')}
              className="w-full py-3 bg-uri-green text-white font-semibold rounded-lg hover:bg-uri-green-dark transition-colors flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
            >
              Más Información
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>

          {/* Hospital Assistance Card */}
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-uri-teal cursor-pointer group">
            <div className="w-16 h-16 bg-uri-teal/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-uri-teal transition-colors">
              <i className="ri-hospital-line text-3xl text-uri-teal group-hover:text-white transition-colors"></i>
            </div>
            
            <h4 className="text-2xl font-bold text-gray-900 mb-4">Servicios de Asistencia Hospitalaria</h4>
            <p className="text-gray-600 mb-6">
              Apoyo profesional y acompañamiento para pacientes en instalaciones de salud tanto públicas como privadas.
            </p>

            {/* Public Hospital Section */}
            <div className="mb-4">
              <button
                onClick={() => setExpandedCard(expandedCard === 'public' ? null : 'public')}
                className="w-full flex items-center justify-between p-4 bg-uri-blue-light/10 rounded-lg hover:bg-uri-blue-light/20 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1 bg-uri-blue text-white text-xs font-semibold rounded-full whitespace-nowrap">Hospitales Públicos</span>
                  <span className="text-sm font-semibold text-gray-900">Servicios en Hospitales Públicos</span>
                </div>
                <i className={`ri-arrow-${expandedCard === 'public' ? 'up' : 'down'}-s-line text-xl text-gray-600`}></i>
              </button>
              
              {expandedCard === 'public' && (
                <div className="mt-3 pl-4 space-y-3">
                  <div className="flex items-start gap-2">
                    <i className="ri-check-line text-uri-blue text-lg mt-0.5 flex-shrink-0"></i>
                    <p className="text-sm text-gray-700"><strong>Asistencia en Emergencias:</strong> Apoyo inmediato en áreas de urgencias durante atención médica crítica</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-check-line text-uri-blue text-lg mt-0.5 flex-shrink-0"></i>
                    <p className="text-sm text-gray-700"><strong>Cuidados Intensivos Adulto (UCI):</strong> Acompañamiento especializado en unidades de cuidados críticos</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-check-line text-uri-blue text-lg mt-0.5 flex-shrink-0"></i>
                    <p className="text-sm text-gray-700"><strong>Cuidados Intensivos Pediátrico (UCIP):</strong> Atención dedicada para niños en estado crítico</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-check-line text-uri-blue text-lg mt-0.5 flex-shrink-0"></i>
                    <p className="text-sm text-gray-700"><strong>Coordinación con Personal Médico:</strong> Comunicación efectiva con doctores, enfermeras y departamentos hospitalarios</p>
                  </div>
                </div>
              )}
            </div>

            {/* Private Center Section */}
            <div className="mb-6">
              <button
                onClick={() => setExpandedCard(expandedCard === 'private' ? null : 'private')}
                className="w-full flex items-center justify-between p-4 bg-uri-green-light/10 rounded-lg hover:bg-uri-green-light/20 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1 bg-uri-green text-white text-xs font-semibold rounded-full whitespace-nowrap">Centros Privados</span>
                  <span className="text-sm font-semibold text-gray-900">Servicios en Centros Privados</span>
                </div>
                <i className={`ri-arrow-${expandedCard === 'private' ? 'up' : 'down'}-s-line text-xl text-gray-600`}></i>
              </button>
              
              {expandedCard === 'private' && (
                <div className="mt-3 pl-4 space-y-3">
                  <div className="flex items-start gap-2">
                    <i className="ri-check-line text-uri-green text-lg mt-0.5 flex-shrink-0"></i>
                    <p className="text-sm text-gray-700"><strong>Asistencia en Emergencias:</strong> Atención inmediata en servicios de urgencias privados</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-check-line text-uri-green text-lg mt-0.5 flex-shrink-0"></i>
                    <p className="text-sm text-gray-700"><strong>Cuidados Intensivos Adulto (UCI):</strong> Acompañamiento premium en unidades de cuidados críticos</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-check-line text-uri-green text-lg mt-0.5 flex-shrink-0"></i>
                    <p className="text-sm text-gray-700"><strong>Cuidados Intensivos Pediátrico (UCIP):</strong> Cuidado especializado para niños en clínicas privadas</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-check-line text-uri-green text-lg mt-0.5 flex-shrink-0"></i>
                    <p className="text-sm text-gray-700"><strong>Coordinación Personalizada:</strong> Comunicación directa con especialistas y equipo médico multidisciplinario</p>
                  </div>
                </div>
              )}
            </div>

            <button 
              onClick={() => scrollToSection('details')}
              className="w-full py-3 bg-uri-teal text-white font-semibold rounded-lg hover:bg-uri-teal-dark transition-colors flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
            >
              Más Información
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
